import { Box, Flex, useMantineTheme } from '@mantine/core';
import type {
	AiTokenUsageTimeSeries,
	AiTokenUsageTimeSeriesPoint,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { unparse } from 'papaparse';
import { memo, useCallback, useMemo } from 'react';
import { BaseOverviewChart } from '../../../../components/Overview/BaseOverviewChart';
import { saveBlob } from '../../../../lib/models/misc';
import type { StackedLineChartChannel } from '../../../../utils/charts/StackedLineChart';
import { dateFilterOptions } from '../../constants';
import type { TimeRangeValue } from '../../types';
import { getGroupKeys, getTopGroupKeys, processTimeseriesData } from './utils';

const MAX_USERS_DISPLAYED = 10;

// Utility function to format numbers with K, M, T abbreviations
const formatNumberWithAbbreviations = (value: number): string => {
	const absValue = Math.abs(value);

	if (value === 0) {
		return '0';
	} else if (absValue < 1000) {
		return value.toString();
	} else if (absValue < 1000000) {
		return `${(value / 1000).toFixed(1)}K`;
	} else if (absValue < 1000000000) {
		return `${(value / 1000000).toFixed(1)}M`;
	} else if (absValue < 1000000000000) {
		return `${(value / 1000000000).toFixed(1)}B`;
	} else {
		return `${(value / 1000000000000).toFixed(1)}T`;
	}
};

export const AiTokenUsageChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
		showAllUsersInTooltip,
	}: {
		timeseries: AiTokenUsageTimeSeries;
		dateFilter: string;
		setDateFilter: (dateFilter: string) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: string;
		setDateRangeOption: (dateRangeOption: string) => void;
		showAllUsersInTooltip: boolean;
	}) => {
		const theme = useMantineTheme();

		// Process timestamps to Date objects and sort by them
		const chartData = useMemo(
			() => processTimeseriesData(timeseries?.points),
			[timeseries]
		);

		// Get all unique group keys from the data
		const groupKeys = useMemo(() => getGroupKeys(chartData), [chartData]);

		// Calculate aggregate tokens per user and get highest users in the time range
		const topGroupKeys = useMemo(
			() =>
				getTopGroupKeys(
					chartData,
					(groupData) => groupData.total_tokens ?? 0,
					MAX_USERS_DISPLAYED
				),
			[chartData, groupKeys]
		);

		// Create channels for each group key or use default total values
		const channels = useMemo(() => {
			const colorPalette = [
				theme.other.getColor('icon/chart/1'),
				theme.other.getColor('icon/chart/2'),
				theme.other.getColor('icon/chart/3'),
				theme.other.getColor('icon/chart/4'),
				theme.other.getColor('icon/chart/5'),
				theme.other.getColor('icon/chart/6'),
				theme.other.getColor('icon/chart/7'),
				theme.other.getColor('icon/chart/8'),
				theme.other.getColor('icon/chart/9'),
				theme.other.getColor('icon/chart/10'),
			];

			// If no group keys, use default total values
			if (topGroupKeys.length === 0) {
				return [
					{
						label: 'Total tokens',
						x: (d: AiTokenUsageTimeSeriesPoint) => new Date(d.timestamp),
						y: (d: AiTokenUsageTimeSeriesPoint) => d.total_tokens ?? 0,
						renderArea: true,
						areaOpacity: 0.3,
						color: colorPalette[0],
						zIndex: 10,
					},
				] as StackedLineChartChannel<Record<string, unknown>, Date, number>[];
			}

			// Otherwise, create channels for each top group key
			return topGroupKeys.map((groupKey, index) => ({
				label: groupKey,
				x: (d: AiTokenUsageTimeSeriesPoint) => new Date(d.timestamp),
				y: (d: AiTokenUsageTimeSeriesPoint) => {
					const groupData = d.group_data?.[groupKey];
					return groupData?.total_tokens ?? 0;
				},
				renderArea: true,
				areaOpacity: 0.3,
				color: colorPalette[index % colorPalette.length],
				zIndex: index + 10,
			})) as StackedLineChartChannel<Record<string, unknown>, Date, number>[];
		}, [topGroupKeys, theme]);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				// Determine which users to show in tooltip
				const usersToShow = showAllUsersInTooltip ? groupKeys : topGroupKeys;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : (
								point.timestamp.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							{usersToShow.length === 0 ? (
								// Show default total values when no group keys
								<>
									<Flex justify="space-between" gap="sm">
										<Flex gap="xs" align="center">
											<Box
												w={16}
												h={2}
												bg={channels[0]?.color}
												style={{
													borderRadius: theme.radius.md,
												}}
												aria-hidden="true"
											/>
											<Text size="sm">Total tokens</Text>
										</Flex>
										<Text fw={500} size="sm">
											{(point.total_tokens ?? 0).toLocaleString()}
										</Text>
									</Flex>
									<Flex justify="space-between" gap="sm">
										<Text size="sm">Input tokens</Text>
										<Text fw={500} size="sm">
											{(point.total_input_tokens ?? 0).toLocaleString()}
										</Text>
									</Flex>
									<Flex justify="space-between" gap="sm">
										<Text size="sm">Output tokens</Text>
										<Text fw={500} size="sm">
											{(point.total_output_tokens ?? 0).toLocaleString()}
										</Text>
									</Flex>
								</>
							) : (
								// Show group data when group keys exist, sorted by usage
								usersToShow
									.map((groupKey) => {
										const groupData = point.group_data?.[groupKey];
										if (!groupData) return null;

										return {
											groupKey,
											groupData,
											usage: groupData.total_tokens,
										};
									})
									.filter(Boolean)
									.sort((a, b) => (b?.usage || 0) - (a?.usage || 0))
									.map((item) => {
										if (!item) return null;
										const { groupKey, groupData } = item;
										const channel = channels.find(
											(ch) => ch.label === groupKey
										);

										return (
											<Flex key={groupKey} justify="space-between" gap="sm">
												<Flex gap="xs" align="center">
													<Box
														w={16}
														h={2}
														bg={channel?.color}
														style={{
															borderRadius: theme.radius.md,
														}}
														aria-hidden="true"
													/>
													<Text size="sm">{groupKey}</Text>
												</Flex>
												<Text fw={500} size="sm">
													{groupData.total_tokens.toLocaleString()}
												</Text>
											</Flex>
										);
									})
							)}
							<Flex justify="space-between" gap="sm">
								<Text size="sm">Total requests</Text>
								<Text fw={500} size="sm">
									{point.total_requests.toLocaleString()}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[
				chartData,
				dateRangeOption,
				groupKeys,
				topGroupKeys,
				channels,
				theme,
				showAllUsersInTooltip,
			]
		);

		// Create a modified timeseries object that includes the required properties
		const modifiedTimeseries = useMemo(
			() => ({
				...timeseries,
				available_channels: [
					'total_tokens',
					'input_tokens',
					'output_tokens',
					'cache_creation_input_tokens',
					'cache_read_input_tokens',
					'requests',
				],
				available_dimensions: ['user_id', 'model_name'],
			}),
			[timeseries]
		);

		// CSV export function
		const handleExportCsv = useCallback(() => {
			if (!chartData.length) return;

			// Prepare CSV data
			const csvData = chartData.map((point) => {
				const baseData = {
					timestamp: point.timestamp.toISOString(),
					total_requests: point.total_requests ?? 0,
				};

				// Add group-specific data if available
				if (topGroupKeys.length === 0) {
					// No groups - use total values
					return {
						...baseData,
						total_tokens: point.total_tokens ?? 0,
						input_tokens: point.total_input_tokens ?? 0,
						output_tokens: point.total_output_tokens ?? 0,
						cache_creation_input_tokens:
							point.total_cache_creation_input_tokens ?? 0,
						cache_read_input_tokens: point.total_cache_read_input_tokens ?? 0,
					};
				} else {
					// Groups available - add each group's data
					const groupData: Record<string, number> = {};
					topGroupKeys.forEach((groupKey) => {
						const groupPoint = point.group_data?.[groupKey];
						groupData[`${groupKey}_cache_creation_input_tokens`] =
							groupPoint?.total_cache_creation_input_tokens ?? 0;
						groupData[`${groupKey}_cache_read_input_tokens`] =
							groupPoint?.total_cache_read_input_tokens ?? 0;
						groupData[`${groupKey}_total_tokens`] =
							groupPoint?.total_tokens ?? 0;
						groupData[`${groupKey}_input_tokens`] =
							groupPoint?.total_input_tokens ?? 0;
						groupData[`${groupKey}_output_tokens`] =
							groupPoint?.total_output_tokens ?? 0;
					});
					return {
						...baseData,
						...groupData,
					};
				}
			});

			// Generate CSV
			const csv = unparse(csvData, {
				escapeFormulae: true,
			});

			// Create filename with date range
			const startDate = chartData[0]?.timestamp.toLocaleDateString([], {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
			const endDate = chartData[
				chartData.length - 1
			]?.timestamp.toLocaleDateString([], {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
			const filename = `ai_token_usage_${startDate}_to_${endDate}.csv`;

			// Download the file
			saveBlob(new Blob([csv], { type: 'text/csv' }), filename);
		}, [chartData, topGroupKeys]);

		return (
			<BaseOverviewChart
				timeseries={modifiedTimeseries}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={dateRangeOption}
				setDateRangeOption={setDateRangeOption}
				title="AI usage"
				dateFilterOptions={dateFilterOptions}
				channels={channels}
				tooltipDataRenderer={tooltipDataRenderer}
				analyticsEventPrefix="ai_token_usage/overview"
				onExportCsv={handleExportCsv}
				showExportButton={true}
				yAxisFormatter={formatNumberWithAbbreviations}
				maxChannels={MAX_USERS_DISPLAYED}
			/>
		);
	}
);
